import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Search, Edit2, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils';

const emptyForm = () => ({ customer_id: '', address: '', description: '', object_number: '' });

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [p, c] = await Promise.all([api.get('/properties'), api.get('/customers')]);
    setProperties(p.data);
    setCustomers(c.data);
  };

  useEffect(() => { load().catch(() => toast.error('Fehler')).finally(() => setLoading(false)); }, []);

  const normalizedSearch = search.trim().toLowerCase();
  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || '–';
  const filtered = properties.filter(p => !normalizedSearch || [
    p.address, p.object_number, p.description, getCustomerName(p.customer_id),
  ].some(v => (v || '').toString().toLowerCase().includes(normalizedSearch)));

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDlgOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ customer_id: p.customer_id || '', address: p.address, description: p.description || '', object_number: p.object_number || '' }); setDlgOpen(true); };

  const handleSave = async () => {
    if (!form.address) { toast.error('Adresse ist pflicht'); return; }
    if (!form.customer_id) { toast.error('Verwaltung ist pflicht'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/properties/${editing.id}`, form);
        toast.success('Aktualisiert');
      } else {
        await api.post('/properties', form);
        toast.success('Liegenschaft erstellt');
      }
      setDlgOpen(false);
      await load();
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    await api.delete(`/properties/${id}`);
    toast.success('Gelöscht');
    await load();
  };

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="properties-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl">Liegenschaften</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{properties.length} Liegenschaften</p>
          </div>
          <Button onClick={openNew} data-testid="new-property-btn" className="rounded-sm gap-2">
            <Plus className="w-4 h-4" />Neue Liegenschaft
          </Button>
        </div>
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen..." className="pl-9 rounded-sm" data-testid="property-search" />
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(p => (
              <div key={p.id} data-testid={`property-card-${p.id}`} className="bg-card border border-border/60 rounded-sm p-5 hover:border-primary/30 hover:shadow-sm transition-all stagger-item">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-foreground">{p.address}</p>
                    {p.object_number && <p className="text-xs text-muted-foreground font-mono">{p.object_number}</p>}
                    <p className="text-xs text-muted-foreground mt-2">{getCustomerName(p.customer_id)}</p>
                    {p.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-sm hover:bg-muted">
                      <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1.5 rounded-sm hover:bg-destructive/10" data-testid={`delete-property-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div className="col-span-3 text-center py-12 text-sm text-muted-foreground">Keine Liegenschaften gefunden</div>}
          </div>
        )}
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="property-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Liegenschaft bearbeiten' : 'Neue Liegenschaft'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Verwaltung *</Label>
              <Select value={form.customer_id} onValueChange={v => setForm(f => ({ ...f, customer_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="property-customer-select"><SelectValue placeholder="Verwaltung auswählen..." /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Adresse *</Label><Input data-testid="property-address-form" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Musterstrasse 1, 8000 Zürich" className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Objektnummer</Label><Input value={form.object_number} onChange={e => setForm(f => ({ ...f, object_number: e.target.value }))} placeholder="z.B. LG-001" className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Beschreibung</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="rounded-sm resize-none" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-property-btn">{saving ? 'Speichern...' : 'Speichern'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
